import * as vp from 'vopay';
import { FunctionComponent, useEffect, useState } from 'react';
import { AddGroupContactDialogueHandler, DialogueHandlerProps } from '../../Dialogue/types';
import usePostAddGroupContact from '@/hooks/useApi/usePostAddGroupContact';
import AddGroupContactDialogueContext, {
  AddGroupContactCtx,
} from '../../Dialogue/AddGroupContact/AddGroupContactContext';
import AddGroupContactDialogue from '../../Dialogue/AddGroupContact/AddGroupContactDialogue';
import { ContactListType } from '../../types';
import { exportAsCSV } from '../../utils';
import { useClientData } from '@/providers/ClientData.provider';
import useFetchClientBankAccounts from '@/hooks/useApi/useFetchClientBankAccounts';
import { useUserSettingsProvider } from '@/providers/UserSettings.provider';
import { useTranslation } from 'react-i18next';

const { Box, Button, Select } = vp;

type ContactFormProps = {
  groupID: string;
  type: string;
  groupList: ContactListType;
  setHasNewContactsList: (e: boolean) => void;
};

type FC = FunctionComponent<ContactFormProps>;

const ContactForm: FC = (props: ContactFormProps) => {
  const { groupID, type, groupList, setHasNewContactsList } = props;
  const { response } = useClientData();

  const [contactAccountsResponse, fetchClientBankAccounts] = useFetchClientBankAccounts({});
  const [contactBankAccounts, setClientBankAccounts] = useState({});
  const [filteredBankAccounts, setFilteredBankAccounts] = useState({});

  const [contactToken, setContactToken] = useState('');
  const [addGroupContactResponse, postAddGroupContact] = usePostAddGroupContact({});
  const [shouldAddGroupContact, setShouldAddGroupContact] = useState(false);
  const [showAddGroupContactErrorAlert, setShowAddGroupContactErrorAlert] = useState(false);
  const [addGroupContactError, setAddGroupContactError] = useState('');
  const { userSettings } = useUserSettingsProvider();
  const { t } = useTranslation('translations', { keyPrefix: 'Groups.Details.Contacts' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });

  const [filteredContactOptions, setFilteredContactOptions] = useState<{ label: string; value: string }[]>([]);
  useEffect(() => {
    const defaultOptions = (Object.values(filteredBankAccounts) as vp.ClientBankAccountsLists[])
      .slice(0, 50)
      .map((account) => ({
        label: account.AccountHolderName,
        value: account.Token,
      }));

    setFilteredContactOptions(defaultOptions);
  }, [filteredBankAccounts]);
  useEffect(() => {
    if (contactAccountsResponse.Success) {
      setClientBankAccounts(contactAccountsResponse.BankAccounts);
    } else if (!contactAccountsResponse.Success && contactAccountsResponse.ErrorMessage) {
      setClientBankAccounts({});
    }
  }, [contactAccountsResponse]);

  useEffect(() => {
    setFilteredBankAccounts(Object.values(contactAccountsResponse?.BankAccounts || {}));
  }, [contactBankAccounts]);

  const handleSearch = (searchTerm: string) => {
    if (!searchTerm) {
      const defaultOptions = (Object.values(filteredBankAccounts) as vp.ClientBankAccountsLists[])
        .slice(0, 50)
        .map((account) => ({
          label: account.AccountHolderName,
          value: account.Token,
        }));
      setFilteredContactOptions(defaultOptions);
      return;
    }
    setFilteredContactOptions(
      (Object.values(filteredBankAccounts) as vp.ClientBankAccountsLists[])
        .filter((account: vp.ClientBankAccountsLists) =>
          account.AccountHolderName.toLowerCase().includes(searchTerm.toLowerCase()),
        )
        .map((account: vp.ClientBankAccountsLists) => ({
          label: account.AccountHolderName,
          value: account.Token,
        }))
        .slice(0, 50),
    );
  };

  const AddGroupContact: AddGroupContactCtx = {
    showAddGroupContactErrorAlert,
    addGroupContactError,
  };

  useEffect(() => {
    fetchClientBankAccounts({
      PortalUser: response.LoginUserName,
      ClientControlled: false,
      Token: '',
      Timezone: userSettings.timezone,
    });
  }, []);

  const handleContactIndex = (e: string) => {
    setContactToken(e);
  };

  const handlers = {
    handleAddGroupContactDialogue: (handlerProps: DialogueHandlerProps<AddGroupContactDialogueHandler>) => {
      const { type: handlerType, state, action = 'default' } = handlerProps;
      const dialogueHandlers: AddGroupContactDialogueHandler = {
        error: {
          confirm: {
            default: () => {
              setShowAddGroupContactErrorAlert(false);
            },
          },
        },
      };
      const dialogueState = dialogueHandlers[handlerType][state];
      const handler = dialogueState[action] || dialogueState.default;
      handler();
    },
  };

  useEffect(() => {
    if (shouldAddGroupContact && contactToken !== '') {
      postAddGroupContact({
        GroupID: groupID || '',
        Token: contactToken,
      });
    } else if (shouldAddGroupContact && contactToken === '') {
      setShouldAddGroupContact(false);
    }
  }, [shouldAddGroupContact, contactToken]);

  useEffect(() => {
    if (shouldAddGroupContact) {
      const { Success, ErrorMessage } = addGroupContactResponse;

      if (Success === true) {
        setHasNewContactsList(true);
        setContactToken('');
      } else if (Success === false) {
        setShowAddGroupContactErrorAlert(true);
        setAddGroupContactError(ErrorMessage);
      }
      setShouldAddGroupContact(false);
    }
  }, [addGroupContactResponse]);

  return (
    <AddGroupContactDialogueContext.Provider value={AddGroupContact}>
      <Box flx="row" jc="sb" mt="md" padding="0 15px 0 0">
        <Box flx="row" gap="sm">
          <Select
            placeholder={t('Form.contactNamePlaceholder')}
            options={filteredContactOptions}
            name="contactName"
            value={contactToken}
            onBlur={() => {}}
            onChange={(e) => {
              handleContactIndex(e.target.value);
            }}
            variant="outlined"
            noOrdering
            enablePagination
            type="selectSearch"
            disableFiltering={false}
            searchCallback={handleSearch}
          />
          <Button
            id="add-group-submit"
            label={type === 'Payment' ? t('Form.submitButtonPayee') : t('Form.submitButtonPayer')}
            type="submit"
            width="140px"
            height="37px"
            onClick={() => {
              setShouldAddGroupContact(true);
            }}
          />
        </Box>

        <Button
          id="export-group-svg"
          label={tGlobal('Buttons.export')}
          color="lined"
          onClick={() => {
            exportAsCSV(groupList, type);
          }}
        />
      </Box>
      <AddGroupContactDialogue handleDialogue={handlers.handleAddGroupContactDialogue} />
    </AddGroupContactDialogueContext.Provider>
  );

  // function buildContactOptions(lists: vp.ClientBankAccountsLists[]) {
  //   return lists.reduce((accumulator, data) => ({ ...accumulator, [data.Token]: `${data.AccountHolderName} ` }), {});
  // }
};

export default ContactForm;
